import React, { Component } from 'react'
import { Route, Switch, Redirect, withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import axios from 'axios'
import moment from 'moment'
import * as _ from 'lodash'
import { Layout, Modal } from 'antd'
import { useMediaQuery } from 'react-responsive';

import './App.scss'
import 'antd/dist/antd.css'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import Header from './shared/Header'
import Footer from './shared/Footer'

import NotFound from './NotFound'
import OrderingClosed from './OrderingClosed'
import Home from './Home'
import Order from './Order'
import OrderStatus from './OrderStatus'
import Drafts from './Drafts'
import Payments from './Payment'
import UserProfile from './UserProfile'
import ForgotPassword from './ForgotPassword'
import ConfirmPassword from './ConfirmPassword'
import PurchaseOrder from "./PurchaseOrder";
import ResetPassword from './ResetPassword'
import {
  fetchDistributor
} from "../actions";
import {
  clearLocalStorage,
  isLocalStorageEmpty,
} from "../utilities";
import config from '../config'


moment.updateLocale(moment.locale(), { invalidDate: 'N/A' })

// Feature flag: when REACT_APP_ORDERING_ENABLED is explicitly "false",
// the whole app is replaced by the "ordering closed" page. Any other
// value (including unset) keeps the site open, so missing config is safe.
const isOrderingClosed =
  String(config.REACT_APP_ORDERING_ENABLED).toLowerCase() === 'false'

const genericErrors = {
  401: {
    title: 'Resource not Found',
    message: 'Unable to load resource'
  },
  500: {
    title: 'Internal Server Error',
    message: 'The server was unable to complete your request.'
  },
  502: {
    title: 'Bad Gateway',
    message: 'The server was unable to complete your request.'
  },
  503: {
    title: 'Service Unavailable',
    message: 'The server was unable to complete your request.'
  }
}

const paths = [{
  default: true,
  exact: true,
  slug: 'home',
  route: '/',
  label: 'Home',
  placements: ['Header', 'Footer'],
  authRequired: false,
  menu: 'left',
  component: Home
},
{
  default: false,
  exact: false,
  activeRequired: true,
  slug: 'order',
  route: '/order',
  label: 'Order Now',
  placements: ['Header'],
  authRequired: true,
  menu: 'left',
  component: Order
},
{
  default: false,
  exact: false,
  slug: 'order-status',
  route: '/order-status',
  label: 'Order Status',
  placements: ['Header'],
  authRequired: true,
  menu: 'left',
  component: OrderStatus
},
{
  default: false,
  exact: false,
  slug: 'drafts',
  route: '/drafts',
  label: 'Drafts',
  placements: ['Header'],
  authRequired: true,
  menu: 'left',
  component: Drafts
},
{
  default: false,
  exact: false,
  slug: 'payments',
  route: '/payments',
  label: 'Payment',
  placements: ['Header'],
  authRequired: true,
  menu: 'left',
  component: Payments
},
{
  default: false,
  exact: false,
  slug: 'forgot-password',
  route: '/forgot-password',
  label: 'Forgot Password',
  placements: [],
  authRequired: false,
  menu: 'left',
  component: ForgotPassword
},
{
  default: false,
  exact: false,
  slug: 'user-profile',
  route: '/user-profile',
  label: 'User Profile',
  placements: ['Header'],
  authRequired: true,
  menu: 'right',
  component: UserProfile
},
{
  default: false,
  exact: false,
  slug: 'confirm-password',
  route: '/confirm-password',
  label: 'Confirm Password',
  placements: [],
  authRequired: true,
  menu: 'right',
  component: ConfirmPassword
},
{
  default: false,
  exact: false,
  slug: "purchase-status",
  route: "/purchase-status",
  label: "Purchase Order",
  placements: ["Header"],
  authRequired: true,
  menu: "right",
  component: PurchaseOrder
},
{
  default: false,
  exact: false,
  slug: "password/reset",
  route: "/password/reset/:token/:email",
  label: "Reset Password",
  placements: [],
  authRequired: true,
  menu: "right",
  component: ResetPassword
}
]

const toastXHRError = code => {
  const error = genericErrors[code]
  if (error) {
    console.error(error.title, error.message)
  }
}

const setCurrentRoute = pathname => dispatch => {
  const payload = _.find(paths, ['route', pathname]);
  dispatch({ type: 'ROUTE_CHANGE', payload });
}

const MainLayout = props => {
  const isMobile = useMediaQuery({ maxWidth: 1224 });
  return <Layout {...props} className={`${props.className} ${isMobile ? 'mobile' : ''}`}>{props.children}</Layout>
}

class App extends Component {
  initialState = {
    loginStatus: false,
    isSessionExpiredModalShow: false
  }

  constructor(props) {
    super(props)
    axios.interceptors.response.use(response => response, (error) => {
      switch (error.response.status) {
        case 401: this.logout(); break
        case 404: toastXHRError(404); break
        case 500: toastXHRError(500); break
        case 502: toastXHRError(502); break
        case 503: toastXHRError(503); break
        default: break
      }
      return Promise.reject(error)
    })

    this.state = this.initialState;
  }

  logout() {
    const self = this
    if (this.state.loginStatus && !this.state.isSessionExpiredModalShow && isLocalStorageEmpty()) {
      this.setState({ isSessionExpiredModalShow: true })
      Modal.info({
        title: 'Your session expired',
        content: (
          <div>
            <p>You will be redirected in home page.</p>
          </div>
        ),
        onOk() {
          clearLocalStorage();
          self.props.history.push("/");
          window.location.reload()
        },
      });
    }
    this.forceUpdate()

  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.props.setCurrentRoute(this.props.location.pathname);
    this.props.fetchDistributor();
  }

  componentDidUpdate(prevProps) {
    const { pathname } = this.props.location;
    if (pathname !== prevProps.location.pathname) {
      window.scrollTo(0, 0);
      this.props.setCurrentRoute(pathname);
    }
  }

  handleLoginStatus = status => {
    this.setState({ ...this.state, loginStatus: status });
  }

  render() {
    const { loginStatus } = this.state;
    const { route } = this.props;

    if (isOrderingClosed) {
      return <OrderingClosed />
    }

    const currentSlug = route.current ? route.current.slug === 'order' ? route.current.slug : '' : '';

    return (
      <MainLayout className={`main-layout ${route.current ? route.current.slug : ''}`} >
        <Header {...this.props} paths={paths} handleLoginStatus={this.handleLoginStatus} />
        <Layout.Content className="main-layout-content">
          <Switch>
            {_.map(paths, path => <Route key={path.slug} exact={path.exact} path={path.route} component={path.component} />)}
            <Route component={NotFound} />
            <Redirect to='/404' />
          </Switch>
        </Layout.Content>
        {currentSlug === '' ?
          <Footer {...this.props} paths={paths} isLoggedIn={loginStatus} />
          : null
        }
      </MainLayout>
    )
  }
}

function mapStateToProps({ route }) {
  return { route }
}

export default withRouter(connect(mapStateToProps, { setCurrentRoute, fetchDistributor })(App))
